import { useContext, useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import Confirm from '../../../components/confirm';
import SaveBackdrop from '../../../components/saveBackdrop';
import Wait from '../../../components/wait';
import { AppContext } from '../../../contexts/app-context';
import GlobalStyles from '../../../global-styles';
import API from '../../../utils/api';
import {
  apiReponseError, apiRequestError, canWriteUser, formatDateTimeForDataGrid, getClientApiKey,
  isNullOrWhiteSpace
} from '../../../utils/helper';
import { CheckIcon, CopyTextIcon, DeleteIcon, LockIcon, LockOpenIcon } from '../../../utils/icons';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton,
  InputLabel, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, Select,
  TextField, Tooltip, Typography
} from '../../../utils/material-ui-core';

interface manageApiKeyCRUDProps {
  open: boolean;
  apiKey: string;
  selectedClientCompanyKey: string;
  onCallback: (confirmOutput: ConfirmOutput) => void;
}
const formDefaultValue = {
  apiKey: "",
  clientCompanyKey: "",
  note: "",
  apiKeyReferenceId: 0,
  apiKeyUserReferenceId: 0,
  clientCompanyName: "",
  email: "",
  firstName: "",
  lastModifiedBy: "",
  lastModifiedDateTime: "",
  lastName: "",
  apiKeyName: "",
  roleName: "",
  userKey: "",
  accessRoles: [{ roleKey: "", name: "", access: false, note: "", createdBy: "", createdDateTime: null }],
};

const ManageApiKeyCRUD = ({ open, apiKey, selectedClientCompanyKey, onCallback }: manageApiKeyCRUDProps) => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { userFunctionAccess, showAlert, referenceLookups } = useContext(AppContext);

  const [newClientCompanyKey, setNewClientCompanyKey] = useState(selectedClientCompanyKey);
  const [note, setNote] = useState("");
  const [dataChanged, setDataChanged] = useState<boolean>(true);
  const [refreshList, setRefreshList] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [isNew, setIsNew] = useState<boolean>(true);
  const [readonly, setReadonly] = useState<boolean>(true);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState<boolean>(false);
  const [resetConfirmDialogOpen, setResetConfirmDialogOpen] = useState<boolean>(false);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [apiKeySecret, setApiKeySecret] = useState<string>("");
  const [clientApiKey, setClientApiKey] = useState<ClientApiKey>(formDefaultValue);

  // Manage api key
  const manageClientApiKey = async () => {
    if (clientApiKey.apiKey !== referenceLookups.newRecordDefaultId) {
      if (!dataChanged) {
        setResetConfirmDialogOpen(true);
      } else {
        await save("");
      }
    } else {
      await save("");
    }
  };

  // Manage api key access roles
  const manageAccess = async (roleKey: string, addAccess: boolean) => {
    if (!canWriteUser(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      cancel();
      return;
    }
    const payload = {
      userKey: clientApiKey.userKey,
      roleKey: roleKey,
    };

    try {
      setSaveInProgress(true);
      await API.post("/api/admin/clientApiKeys/" + clientApiKey.apiKey + "/access", payload)
        .then(async (response) => {
          if (addAccess) {
            showAlert({ message: "Successfully added.", severity: "success" });
          } else {
            showAlert({ message: "Successfully removed.", severity: "success" });
          }

          setClientApiKey(formDefaultValue);
          await getClientApiKeyData();
          setSaveInProgress(false);
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          showAlert({
            message: apiReponseError(error, "Failed to save/delete user access."),
            severity: "error",
          });
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  // Reset api key confirmation
  const resetClientApiKeyConfirmation = async (confirm: boolean) => {
    setResetConfirmDialogOpen(false);
    if (confirm) {
      await save("reset");
    }
  };

  // Save client api key
  const save = async (path: string) => {
    if (!canWriteUser(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      cancel();
      return;
    }

    let payload = {
      clientCompanyKey: clientApiKey.clientCompanyKey,
      note: note,
    };

    if (clientApiKey.apiKey === referenceLookups.newRecordDefaultId && selectedClientCompanyKey !== "") {
      payload = {
        clientCompanyKey: selectedClientCompanyKey,
        note: note,
      };
    } else if (clientApiKey.apiKey !== referenceLookups.newRecordDefaultId) {
      payload = {
        clientCompanyKey: newClientCompanyKey,
        note: note,
      };
    }

    try {
      setSaveInProgress(true);
      setApiKeySecret("");

      const url = "/api/admin/clientApiKeys/" + clientApiKey.apiKey + (path !== "" ? "/" + path : "");

      await API.post(url, payload)
        .then(async (response) => {
          showAlert({ message: "Successfully saved.", severity: "success" });

          if (response.data.secretKey !== null) {
            setApiKeySecret(response.data.secretKey);
          }

          let tempFormDefaultValue = JSON.parse(JSON.stringify(formDefaultValue));
          tempFormDefaultValue.apiKey = response.data.apiKey;
          setClientApiKey(tempFormDefaultValue);
          apiKey = response.data.apiKey;
          selectedClientCompanyKey = "";
          setIsNew(false);
          setDataChanged(false);
          setRefreshList(true);
          await getClientApiKeyData();
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          showAlert({
            message: apiReponseError(error, "Failed to save api key data."),
            severity: "error",
          });
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  // Open delete confirmation dialog
  const openDeleteClientApiKeyConfirmation = () => {
    if (!canWriteUser(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      cancel();
      return;
    }
    setDeleteConfirmDialogOpen(true);
  };

  // Delete  api key confirmation
  const deleteClientApiKeyConfirmation = async (confirm: boolean) => {
    setDeleteConfirmDialogOpen(false);

    if (confirm) {
      try {
        await API.delete("/api/admin/clientApiKeys/" + clientApiKey.apiKey)
          .then(async (response) => {
            showAlert({
              message: "Successfully removed.",
              severity: "success",
            });
            setRefreshList(true);
            cancel();
            return;
          })
          .catch((error) => {
            showAlert({
              message: apiReponseError(error, "Failed to delete api key data."),
              severity: "error",
            });
          });
      } catch (e) {
        showAlert({
          message: apiRequestError(e, "Something went wrong in our system."),
          severity: "error",
        });
      }
    }
  };

  // Cancel dialog
  const cancel = () => {
    if (refreshList) {
      onCallback({ status: "refresh", data: "" });
      return;
    }
    onCallback({ status: "cancel", data: "" });
  };

  // Get client api key data
  const getClientApiKeyData = async () => {
    const apiResponse = await getClientApiKey(apiKey);
    if (apiResponse.success) {
      const data = apiResponse.data;
      setClientApiKey(data.clientApiKey);
      setNote(data.clientApiKey.note);
      setLoading(false);
      setSaveInProgress(false);
    } else {
      setLoading(true);
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      cancel();
      return;
    }
  };

  useEffect(() => {
    const initialPageAsync = async () => {
      if (open) {
        setReadonly(true);
        setDataChanged(false);
        setLoading(false);
        setSaveInProgress(false);
        setIsNew(false);
        setApiKeySecret("");
        setNote("");
        if (isNullOrWhiteSpace(apiKey)) {
          showAlert({
            message: "Api key is missing.",
            severity: "error",
          });
          cancel();
          return;
        }

        if (!canWriteUser(userFunctionAccess)) {
          showAlert({ message: "Access Denied.", severity: "error" });
          cancel();
          return;
        }

        if (apiKey === referenceLookups.newRecordDefaultId) {
          let tempFormDefaultValue = JSON.parse(JSON.stringify(formDefaultValue));
          tempFormDefaultValue.apiKey = referenceLookups.newRecordDefaultId;
          setClientApiKey(tempFormDefaultValue);
          setIsNew(true);
          setLoading(false);
          setReadonly(false);
        } else {
          setLoading(true);
          setClientApiKey(formDefaultValue);
          await getClientApiKeyData();
          setIsNew(false);
          setReadonly(false);
        }
      }
    };

    initialPageAsync();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, apiKey]);

  return (
    <>
      <Confirm title="Confirmation" content={`<p style='color:red'><b>When deleting the client API key, the old api key will no longer work.</b><p/><p>Are you sure you want to delete this api key?</p>`} yesText="DELETE" noText="CANCEL" open={deleteConfirmDialogOpen} callbackOnClose={() => setDeleteConfirmDialogOpen} callbackOnConfirm={deleteClientApiKeyConfirmation} />

      <Confirm title="Confirmation" content={`<p style='color:red'><b>When resetting the client API key, the old api key will be deleted and will no longer work.</b><p/><p>Are you sure you want to reset this api key?</p>`} yesText="RESET" noText="CANCEL" open={resetConfirmDialogOpen} callbackOnClose={() => setResetConfirmDialogOpen} callbackOnConfirm={resetClientApiKeyConfirmation} />

      <Dialog className={globalStyles.dialogContainer} open={open} maxWidth={"sm"} fullWidth={true}>
        <DialogTitle id="input-dialog-title" className={globalStyles.dialogTitleContainer}>
          <Typography variant="subtitle1" component="div">
            Manage API key & Permissions
          </Typography>
        </DialogTitle>
        {loading ? (
          <>
            <DialogContent dividers>
              <Wait />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => cancel()} color="secondary" autoFocus>
                <u>Cancel</u>
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent dividers className={globalStyles.formikContainer}>
              <SaveBackdrop open={saveInProgress} />

              <Box className={pageStyles.contentText}>
                <Grid container spacing={3}>
                  {isNew ? (
                    <>
                      <Grid item xs={12}>
                        <FormControl fullWidth={true}>
                          <InputLabel id="parent-id-label" shrink={true}>
                            Access To
                          </InputLabel>
                          <Select
                            native
                            labelId="client-company-key-label"
                            id="newClientCompanyKey"
                            name="newClientCompanyKey"
                            value={newClientCompanyKey}
                            // disabled={selectedClientCompanyKey !== "" ? true : false}
                            disabled={true}
                            onChange={(e) => {
                              setNewClientCompanyKey(e.currentTarget.value as string);
                              setDataChanged(true);
                            }}
                          >
                            <option value="">All Companies</option>
                            <optgroup label="Companies">
                              {referenceLookups.clientCompanies.map((clientCompany, index) => (
                                <option value={clientCompany.clientCompanyKey} key={index}>
                                  {clientCompany.name}
                                </option>
                              ))}
                            </optgroup>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Note"
                          value={note}
                          id="note"
                          name="note"
                          onChange={(e) => {
                            setNote(e.currentTarget.value as string);
                            setDataChanged(true);
                          }}
                        />
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12}>
                        <TextField disabled={true} label="Access To" value={clientApiKey.clientCompanyKey !== "" ? clientApiKey.clientCompanyName : "All Companies"} />
                      </Grid>

                      <Grid item xs={6}>
                        <TextField disabled={true} label="First Name" value={clientApiKey.firstName} />
                      </Grid>

                      <Grid item xs={6}>
                        <TextField disabled={true} label="Last Name" value={clientApiKey.lastName} />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField disabled={true} label="Email" value={clientApiKey.email} />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField disabled={true} label="Role" value={clientApiKey.roleName} />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField disabled={true} label="API Key Name" value={clientApiKey.apiKeyName} />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Note"
                          value={note}
                          id="note"
                          name="note"
                          onChange={(e) => {
                            setNote(e.currentTarget.value as string);
                            setDataChanged(true);
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Box className={globalStyles.topic}>Audit</Box>
                      </Grid>

                      <Grid item xs={6}>
                        <TextField disabled={true} label="Last Modified" value={clientApiKey.lastModifiedBy} />
                      </Grid>

                      <Grid item xs={6}>
                        <TextField disabled={true} label="Last Modified On" value={clientApiKey.lastModifiedDateTime !== "" ? formatDateTimeForDataGrid(clientApiKey.lastModifiedDateTime) : "  "} />
                      </Grid>
                    </>
                  )}

                  {apiKeySecret !== "" ? (
                    <Grid item xs={12}>
                      <Box justifyItems="center" width={1}>
                        <Box p={2} className={pageStyles.apikeyTitle}>
                          To copy the API key secret, click <b>Copy to clipboard</b>.
                        </Box>
                        <Box p={2} className={pageStyles.apikeyKey} display="flex" justifyContent="center" flexDirection="row">
                          <Grid container>
                            <Grid item xs={10}>
                              {apiKeySecret}
                            </Grid>
                            <Grid item xs={2}>
                              <Button
                                onClick={() => {
                                  navigator.clipboard.writeText(apiKeySecret);
                                  showAlert({
                                    message: "Copy to clipboard.",
                                    severity: "success",
                                  });
                                }}
                              >
                                <CopyTextIcon fontSize="small" />
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box p={2} className={pageStyles.apikeyNote}>
                          Store this api key secret in a safe place until you can deliver this to the client company. After you click Close, the api key secret cannot be retrieved again.
                        </Box>
                      </Box>
                      <Box p={2}>&nbsp;</Box>
                    </Grid>
                  ) : null}

                  {!isNew && !dataChanged ? (
                    <>
                      <Grid item xs={12}>
                        <Box className={globalStyles.topic}>Endpoint Access List</Box>
                        <Typography variant="subtitle1">Click the 'Lock/Unlock' button to select the appropriate endpoint access.</Typography>
                      </Grid>

                      <Grid item xs={12}>
                        {clientApiKey.apiKey !== "" ? (
                          <List>
                            {clientApiKey.accessRoles.map((accessRole, index) => (
                              <ListItem key={index}>
                                <ListItemIcon>{accessRole.access ? <CheckIcon /> : null}</ListItemIcon>
                                <ListItemText
                                  primary={accessRole.name}
                                  secondary={
                                    <div>
                                      <div>{accessRole.note}</div>
                                      <div style={{ fontSize: 12, color: "brown", textTransform: "uppercase" }}>
                                        <small>
                                          {accessRole.createdBy}
                                          {accessRole.createdDateTime !== "" ? " - " + formatDateTimeForDataGrid(accessRole.createdDateTime) : "  "}
                                        </small>
                                      </div>
                                    </div>
                                  }
                                  secondaryTypographyProps={{ component: "div" }}
                                />
                                <ListItemSecondaryAction>
                                  {!readonly ? (
                                    <>
                                      {accessRole.access ? (
                                        <IconButton edge="end">
                                          <Tooltip arrow title="Remove access">
                                            <LockOpenIcon fontSize="medium" color="secondary" onClick={() => manageAccess(accessRole.roleKey, false)} />
                                          </Tooltip>
                                        </IconButton>
                                      ) : (
                                        <IconButton edge="end">
                                          <Tooltip arrow title="Add access">
                                            <LockIcon fontSize="medium" color="error" onClick={() => manageAccess(accessRole.roleKey, true)} />
                                          </Tooltip>
                                        </IconButton>
                                      )}
                                    </>
                                  ) : null}
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                          </List>
                        ) : null}
                        <Box p={2}></Box>
                      </Grid>
                    </>
                  ) : null}
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions>
              {!dataChanged && !isNew ? (
                <Button onClick={() => openDeleteClientApiKeyConfirmation()} className={globalStyles.errorText}>
                  <DeleteIcon />
                </Button>
              ) : null}

              {!dataChanged ? (
                <Button onClick={() => manageClientApiKey()} color="secondary" variant="contained" className={globalStyles.buttonDefault}>
                  {isNew ? "Create" : "Reset"}
                </Button>
              ) : (
                <Button onClick={() => manageClientApiKey()} color="secondary" variant="contained" className={globalStyles.buttonDefault}>
                  {isNew ? "Create" : "Update"}
                </Button>
              )}

              <Button onClick={() => cancel()} autoFocus className={globalStyles.buttonDefault}>
                <u>Close</u>
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentText: {
      whiteSpace: "pre-wrap",
      minHeight: "400px",
    },
    apikeyTitle: {
      fontSize: "14px",
      textAlign: "center",
    },
    apikeyNote: {
      fontSize: "1wpx",
      textAlign: "left",
    },
    apikeyKey: {
      fontSize: "20px",
      fontWeight: "bold",
      textAlign: "center",
      borderWidth: 1,
      borderColor: theme.palette.grey[300],
      background: theme.palette.grey[200],
      borderStyle: "solid",
    },
  })
);

export default ManageApiKeyCRUD;
